import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Hero: React.FC = () => {
  const [currentVideoIndex] = useState(0);
  const [homepageVectorLoaded, setHomepageVectorLoaded] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const videos = [
    '/bargad video.mp4'
  ];

  useEffect(() => {
    const currentVideo = videoRefs.current[currentVideoIndex];
    if (currentVideo) {
      currentVideo.play();
    }
  }, [currentVideoIndex]);

  // Preload homepage vector to prevent refresh issues
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      // Add a small delay to ensure smooth transition
        setTimeout(() => {
          setHomepageVectorLoaded(true);
        }, 100);
    };
    img.onerror = () => {
      setHomepageVectorLoaded(true); // Still show the image even if preload fails
    };
    img.src = '/web vectors/web vectors/web vector 2.png';
    
    // Fallback timeout to ensure image shows even if preload fails
    const fallbackTimeout = setTimeout(() => {
      if (!homepageVectorLoaded) {
        setHomepageVectorLoaded(true);
      }
    }, 2000);
    
    return () => clearTimeout(fallbackTimeout);
  }, [homepageVectorLoaded]);

  const handleVideoEnd = () => {
    // Since we only have one video, just restart it
    const currentVideo = videoRefs.current[currentVideoIndex];
    if (currentVideo) {
      currentVideo.currentTime = 0;
      currentVideo.play();
    }
  };


  return (
    <div>
      {/* Hero Section */}
      <section id="hero" className="h-screen bg-black relative overflow-hidden -mt-0">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          {videos.map((videoSrc, index) => (
            <video 
              key={index}
              ref={(el) => {
                videoRefs.current[index] = el;
              }}
              className={`w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentVideoIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ 
                objectPosition: 'center center', 
                objectFit: 'cover'
              }}
              poster="/api-dev-portal.png"
              autoPlay
              muted
              loop
              playsInline
              onEnded={handleVideoEnd}
            >
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ))}
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 min-h-[80vh] xl:min-h-screen flex items-center pt-[9rem] lg:pt-[8rem] xl:pt-[8rem] pb-[2.5rem] xl:pb-0">
          <div className="container mx-auto px-[1.5rem]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[1.5rem] xl:gap-[2.5rem] items-center">
              {/* Right Side - Homepage Vector (shown first on mobile) */}
              <div className="flex justify-center lg:justify-center lg:items-start order-1 lg:order-2">
                <div className="relative w-full max-w-[18rem] lg:max-w-[32rem] xl:max-w-[40rem] lg:-mt-8 xl:-mt-8 2xl:-mt-6">
                  <img 
                    src="/web vectors/web vectors/web vector 2.png" 
                    alt="Bargad AI Technology Visualization" 
                    className={`w-full h-auto object-contain transition-opacity duration-1000 ease-in-out max-h-[280px] lg:max-h-[450px] ${homepageVectorLoaded ? 'opacity-100' : 'opacity-0'}`}
                    loading="eager"
                    decoding="async"
                    style={{
                      objectFit: 'contain',
                      objectPosition: 'center center',
                      width: '100%',
                      height: 'auto',
                      maxWidth: '100%'
                    }}
                    onLoad={() => {
                      // Ensure smooth transition by setting loaded state
                      if (!homepageVectorLoaded) {
                        setHomepageVectorLoaded(true);
                        console.log('Homepage vector loaded successfully');
                      }
                    }}
                    onError={(e) => {
                      console.error('Failed to load homepage vector:', e);
                      setHomepageVectorLoaded(true);
                    }}
                  />
                </div>
              </div>

              {/* Left Side - Text Content (shown second on mobile, centered) */}
              <div className="space-y-[0.875rem] xl:space-y-[1.5rem] order-2 lg:order-1 text-center lg:text-left">
                {/* Main Headline */}
                <div className="space-y-0.5 xl:space-y-1">
                  <h1 className="text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] font-black leading-tight tracking-tight">
                    <span className="text-[#ffffff] block text-[0.6875rem] lg:text-[0.75rem] xl:text-[1rem] tracking-widest whitespace-nowrap">advanced real time</span>
                    <span className="text-[#26ab4d] block text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] whitespace-nowrap">RISK DECISIONING</span>
                    <span className="text-[#26ab4d] block text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] mb-1.5 xl:mb-3 whitespace-nowrap">PLATFORM</span>
                    <span className="text-[#ffffff] block text-[0.6875rem] lg:text-[0.75rem] xl:text-[1rem] tracking-widest whitespace-nowrap">designed to combat</span>
                    <span className="text-[#ff0000] block text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] flex items-center justify-center lg:justify-start mb-1 xl:mb-2 whitespace-nowrap">
                      INTENT TO FR
                      <img 
                        src="/hacker.png" 
                        alt="A" 
                        className="w-[1.5rem] h-[1.5rem] lg:w-[1.75rem] lg:h-[1.75rem] xl:w-[3rem] xl:h-[3rem] -mx-1 xl:-mx-2 object-contain"
                        style={{ 
                          verticalAlign: 'baseline'
                        }}
                      />
                      UD
                    </span>
                  </h1>
                </div>

                {/* Supporting Text */}
                <p className="text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] max-w-lg mx-auto lg:mx-0 leading-relaxed tracking-wider">
                  Bargad uses AI and graph analytics to detect anomalies, preventing identity fraud and mule accounts creation.
                </p>

                {/* CTA Button */}
                <div className="pt-1 xl:pt-2 flex justify-center lg:justify-start" id="try-it-button">
                  <a href="/try-it-yourself" className="bg-transparent border-2 border-[#26ab4d] text-[#26ab4d] px-4 lg:px-5 xl:px-8 py-2 lg:py-2.5 xl:py-4 rounded-full text-xs lg:text-sm xl:text-lg font-bold hover:bg-[#26ab4d] hover:text-[#ffffff] transition-all duration-300 shadow-xl inline-flex items-center justify-center min-w-[120px] lg:min-w-[140px] xl:min-w-[170px]">
                    Quick preview
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hypothesis Section */}
      <div className="bg-black relative overflow-hidden pt-0 border-t-0 lg:border-t-0">
        
        
        <div className="container mx-auto px-[1.5rem] relative z-10 py-8">
          <div className="w-full">
            
            <div className="flex flex-col justify-start pt-0">
              
              {/* Title */}
              <div className="text-center pt-8 mb-6 flex justify-center items-center w-full">
                <h2 className="text-[1.375rem] sm:text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] font-black text-[#ffffff] leading-tight text-center break-words">
                      What is our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">hypothesis</span>?
                    </h2>
              </div>
              
              <div className="w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
                  
                  {/* Left Side - Acquisition Funnel Image */}
                  <div className="flex justify-center items-center overflow-hidden">
                    <div className="relative w-full">
                      <video 
                        src="/ACQUISITION (12).mp4"
                        className="w-full h-auto object-contain rounded-2xl"
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{ transform: 'scale(1.0)', transformOrigin: 'center center' }}
                      />
                              </div>
                            </div>
                            
                  {/* Right Side - Text Description */}
                  <div className="flex flex-col h-full pl-8">
                    
                    {/* Problem Statement Heading */}
                    <div className="text-left mb-6">
                      <h3 className="text-[1.125rem] sm:text-[1.25rem] lg:text-[1.5rem] xl:text-[2rem] font-bold text-[#26ab4d] break-words">Problem Statement</h3>
                    </div>
                    
                    {/* Description Text - Left Aligned */}
                    <div className="mb-0">
                      <p className="text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] leading-relaxed tracking-wider text-left">
                        Most fraud and identity theft start at the onboarding stage, where stolen or fake identities slip through undetected. Traditional fraud monitoring begins only at the transaction level when the damage is already done. This creates a critical gap that allows mule accounts to enter the system. Bargad.ai fills this gap by detecting risky users early, right at the acquisition layer.
                      </p>
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What are we proposing? Section */}
      <section className="bg-black relative overflow-hidden pt-0 border-t-0 lg:border-t-0">
        <div className="container mx-auto px-[1.5rem] relative z-10 py-8">
          <div className="w-full">
            <div className="flex flex-col justify-start pt-0">
              
              {/* Title */}
              <div className="text-center pt-16 mb-6 flex justify-center items-center w-full">
                <h2 className="text-[1.375rem] sm:text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] font-black text-[#ffffff] leading-tight text-center break-words">
                  What are we <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">proposing</span>?
                </h2>
              </div>
              
              <div className="w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
                  
                  {/* Left Side - KYC Image */}
                  <div className="flex justify-center items-center overflow-hidden">
                    <img 
                      src="/web vectors/web vectors/kyc.png" 
                      alt="KYC" 
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 70%', transform: 'scaleY(1.2)', transformOrigin: 'center center' }}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  
                  {/* Right Side - Content */}
                  <div className="flex flex-col h-full pl-8">
                    
                    {/* Bargad's KYC++ Model Heading */}
                    <div className="text-left mb-6">
                      <h3 className="text-[1.125rem] sm:text-[1.25rem] lg:text-[1.5rem] xl:text-[2rem] font-bold text-[#26ab4d] break-words">Bargad's KYC++ Model</h3>
                    </div>
                    
                    {/* Description Text - Left Aligned */}
                    <div className="mb-4 lg:mb-6">
                      <p className="text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] leading-relaxed tracking-wider text-left">
                        We propose adding an advanced KYC++ layer that works seamlessly on top of your existing KYC. It analyzes user device, geography, network quality, and cognitive behaviour. This helps identify hidden fraud patterns missed by traditional KYC. Only genuine users move forward in your onboarding funnel.
                      </p>
                    </div>
                    
                    {/* KYC++ Image */}
                    <div className="flex justify-center items-center w-full flex-grow overflow-hidden">
                      <img 
                        src="/KYC-Layer.png"
                        alt="Bargad KYC++ Model" 
                        className="w-full h-full object-contain object-center scale-105 lg:scale-90"
                        style={{ transformOrigin: 'center center' }}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Source Data Integration Section */}
      <div className="bg-black relative overflow-hidden border-t-0 lg:border-t-0">
        <div className="container mx-auto px-[1.5rem] relative z-10 pt-8 pb-[4rem]">
          <div className="w-full">
              <div className="text-center pt-16 mb-12">
              <h2 className="text-[1.375rem] sm:text-[1.5rem] lg:text-[1.75rem] xl:text-[3rem] font-black text-[#ffffff] mb-6 sm:mb-8 leading-tight break-words">
                  9 Box <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">Model</span>
              </h2>
                <p className="text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-[#ffffff] max-w-4xl mx-auto leading-relaxed tracking-wider">
                  Our platform leverages a triangulation of diverse data sources, GNN capabilities to create comprehensive risk profile enabling business decisions
                </p>
              </div>

            <div className="w-full">
              <div className="grid grid-cols-1 tablet:grid-cols-3 gap-3 tablet:gap-4 justify-items-center max-w-[600px] mx-auto mb-8">
                {/* Row 1 */}
                <Link to="/products/digital-footprints" className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/digital footprint.png" alt="Digital Footprints" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Digital Footprints</h5>
                </Link>
                
                <Link to="/products/cognitive-behaviour"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/cognitive behaviour.png" alt="Cognitive Behaviour" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Cognitive Behaviour</h5>
                </Link>
                
                <Link to="/products/device-forensic"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/device forensic.png" alt="Device Forensic" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Device Forensic</h5>
                </Link>
                
                {/* Row 2 */}
                <Link to="/products/geolocation"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/geo location.png" alt="Geolocation" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      if (!el.dataset.fallbackTried) {
                        el.dataset.fallbackTried = '1';
                        el.src = '/nine-box-model/geolocation.png';
                      } else {
                        el.src = '/design1.png';
                      }
                    }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Geolocation</h5>
              </Link>
                  
                <Link to="/products/network-quality"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/network connectivity.png" alt="Network Connectivity" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Network Quality</h5>
      </Link>
                    
                <Link to="/products/mobile-to-sources"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/mobile%20to%20sources.png" alt="Mobile to sources" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      if (!el.dataset.fallbackTried) {
                        el.dataset.fallbackTried = '1';
                        el.src = '/nine-box-model/mobile to sources.png';
                      } else {
                        el.src = '/design1.png';
                      }
                    }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Mobile to sources</h5>
                      </Link>
                      
                {/* Row 3 */}
                <Link to="/products/litigation-lookup"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/litigation lookup.png" alt="Litigation Lookup" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Litigation Lookup</h5>
                      </Link>
                      
                <Link to="/products/document-forged"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/doc forged.png" alt="Document Forged" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Document Forged</h5>
                      </Link>
                      
                <Link to="/products/transaction-patterns"                 className="bg-gradient-to-br from-[#26ab4d]/20 to-[#1e8a3f]/20 rounded-xl p-3 tablet:p-[1rem] border border-[#26ab4d]/30 text-center aspect-square flex flex-col justify-center w-[8rem] h-[8rem] tablet:w-[12rem] tablet:h-[12rem] hover:scale-105 transition-transform duration-300">
                  <div className="flex items-center justify-center mb-1.5 tablet:mb-2">
                    <img src="/nine-box-model/transaction patters.png" alt="Transaction Patterns" className="w-[2.5rem] h-[2.5rem] tablet:w-[3.5rem] tablet:h-[3.5rem] object-contain" loading="lazy" decoding="async" width="56" height="56" onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/design1.png'; }} />
                  </div>
                  <h5 className="text-[0.75rem] tablet:text-[0.875rem] font-semibold text-[#ffffff]">Transaction Patterns</h5>
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};


export default Hero;